import { humanDefaults, occupiedDefaults } from '../s2/defaults';
import {
  HUMAN_ROLES,
  OCCUPIED_APPROACHES,
  OCCUPIED_DISPOSITIONS,
  OCCUPIED_GATES,
  OCCUPIED_STANCES,
  type CrewDef,
  type HumanDef,
  type OccupiedRulesDef,
  type OccupiedTellDef,
} from '../s2/types';
import type { Err, Ids } from './common';
import { filled, idsIn, idsOf, isChance, sectionList } from './s2common';
import type { Bundle } from './types';

/** `HumanCombatRules.MinTelegraphS`: every shot is telegraphed at least this long. */
export const MIN_TELEGRAPH_S = 0.5;

const DISPOSITIONS: readonly string[] = OCCUPIED_DISPOSITIONS;
/** `HumanRoles.Of`: the role, or the id when it has none. */
export const humanRole = (h: HumanDef | null | undefined) =>
  h == null ? '' : h.role ? h.role : (h.id ?? '');
const shoots = (role: string) => role === 'gunner' || role === 'leader';
/** `OccupiedRules.IsShelter`: a crew that lives in a school (the Homeroom). */
export const isShelter = (c: CrewDef) =>
  (c.osmTags ?? []).some((t) => t != null && t.toLowerCase().includes('school'));
/** A child look or archetype (`ContentValidator.IsChildLook`). */
const isChildLook = (id: string | null | undefined) =>
  !!id && (id.toLowerCase().startsWith('child') || id.toLowerCase().includes('_child'));

/**
 * Port of shared-cs `ContentValidator.Occupied.cs` (`OccupiedSections`: occupied, crews, humans, tells), S2-HUM. Same errors, same
 * wording, same order. Entries are always checked; the rules block's grid, categories and crews only when the feature is enabled.
 */
export function validateOccupied(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const humans = sectionList<HumanDef>(b.humans, 'humans', err, 'humans');
  const crews = sectionList<CrewDef>(b.crews, 'crews', err, 'crews');
  const tells = sectionList<OccupiedTellDef>(b.tells, 'tells', err, 'tells');
  if (b.occupied === null) err('occupied', undefined, '', "Bundle is missing 'occupied'.");
  const rules = filled(
    occupiedDefaults(),
    (b.occupied ?? undefined) as OccupiedRulesDef | undefined,
  );

  const items = idsIn(b.items);
  const tables = idsIn(b.lootTables);
  const roles = idsIn(b.backgrounds); // background ids (recruit roles are retired, card 11)

  const humanIds = idsOf(humans, (h) => h.id, 'human', err, 'humans');
  for (const [h, i] of humans)
    human(filled(humanDefaults(), h), items, (p, m) => err('humans', i, p, m));
  idsOf(crews, (c) => c.id, 'crew', err, 'crews');
  for (const [c, i] of crews)
    crew(c, b, rules, humanIds, tables, roles, (p, m) => err('crews', i, p, m));
  idsOf(tells, (t) => t.id, 'tell', err, 'tells');
  for (const [t, i] of tells) tell(t, (p, m) => err('tells', i, p, m));
  rulesBlock(rules, b, roles, (p, m) => err('occupied', undefined, p, m));
}

type At = (path: string, message: string) => void;

function human(h: Required<HumanDef>, items: Set<string>, at: At) {
  const w = `Human '${h.id}'`;
  const role = humanRole(h);
  if (!(HUMAN_ROLES as readonly string[]).includes(role))
    at('role', `${w} has unknown role '${role}' (resident, gunner, rusher or leader).`);
  if (h.health <= 0) at('health', `${w} health must be above 0.`);
  if (h.weaponId && !items.has(h.weaponId))
    at('weaponId', `${w} has unknown weapon '${h.weaponId}'.`);
  if (h.armorId && !items.has(h.armorId)) at('armorId', `${w} has unknown armor '${h.armorId}'.`);
  if (shoots(role)) {
    if (!h.weaponId) at('weaponId', `${w} shoots, so it needs a weaponId.`);
    if (h.telegraphS < MIN_TELEGRAPH_S)
      at(
        'telegraphS',
        `${w} telegraphS must be at least ${MIN_TELEGRAPH_S} (every shot is telegraphed).`,
      );
    if (h.fireIntervalS <= h.telegraphS)
      at('fireIntervalS', `${w} fireIntervalS must be longer than its telegraphS.`);
  }
  if (role === 'rusher' && h.meleeDamage <= 0)
    at('meleeDamage', `${w} is a rusher, so it needs meleeDamage above 0.`);
  if (!isChance(h.hitNear) || !isChance(h.hitFar))
    at('hitNear', `${w} hitNear and hitFar must be in 0..1.`);
  if (h.hitFar > h.hitNear) at('hitFar', `${w} hitFar can't be above hitNear.`);
  if (h.nearM < 0 || h.farM < h.nearM) at('nearM', `${w} needs 0 ≤ nearM ≤ farM.`);
  if (h.sightHalfAngleDeg <= 0 || h.sightHalfAngleDeg > 180)
    at('sightHalfAngleDeg', `${w} sightHalfAngleDeg must be in (0, 180].`);
  if (h.sightRangeM <= 0) at('sightRangeM', `${w} sightRangeM must be above 0.`);
  if (h.walkMps <= 0 || h.runMps < h.walkMps) at('walkMps', `${w} needs 0 < walkMps ≤ runMps.`);
  if (h.morale <= 0 || h.moraleFloor < 0 || h.moraleFloor > h.morale)
    at('morale', `${w} needs 0 ≤ moraleFloor ≤ morale and morale above 0.`);
  if (h.headshotMult < 0) at('headshotMult', `${w} headshotMult is negative.`);
  if (!isChance(h.knockbackResist)) at('knockbackResist', `${w} knockbackResist must be in 0..1.`);
}

function crew(
  c: CrewDef,
  b: Bundle,
  rules: Required<OccupiedRulesDef>,
  humanIds: Set<string>,
  tables: Set<string>,
  roles: Set<string>,
  at: At,
) {
  const w = `Crew '${c.id ?? ''}'`;
  if (!(c.name ?? '').trim()) at('name', `${w} needs a name.`);
  if (!c.osmTags || c.osmTags.length === 0)
    at('osmTags', `${w} needs osmTags (the building kinds it lives in).`);
  let total = 0;
  for (const [d, v] of Object.entries(c.dispositions ?? {})) {
    if (!DISPOSITIONS.includes(d)) at('dispositions', `${w} has unknown disposition '${d}'.`);
    if (v < 0) at('dispositions', `${w} disposition '${d}' has a negative weight.`);
    else total += v;
  }
  if (total <= 0) at('dispositions', `${w} needs at least one disposition with a weight above 0.`);
  if (isShelter(c))
    for (const d of ['hostile', 'cannibal'])
      if ((c.dispositions?.[d] ?? 0) > 0)
        at('dispositions', `${w} lives in a school, so it can't roll ${d}.`);

  // The C# reads b.Occupied?.MaxHumansAlive ?? 6: an explicit null block counts as 6.
  const max = Math.max(1, b.occupied === null ? 6 : rules.maxHumansAlive);
  const hc = c.headcount ?? [];
  if (hc.length > 0) {
    if (hc.length !== 2 || hc[0]! < 1 || hc[1]! < hc[0]!)
      at('headcount', `${w} headcount must be [min, max] with 1 ≤ min ≤ max.`);
    else if (hc[1]! > max)
      at('headcount', `${w} headcount can't be above maxHumansAlive (${max}).`);
  }

  if (!c.roster || c.roster.length === 0) at('roster', `${w} needs a roster.`);
  let leaders = 0;
  (c.roster ?? []).forEach((m, k) => {
    if (m == null) {
      at(`roster.${k}`, `${w} has an empty roster entry.`);
      return;
    }
    const archetype = m.archetype ?? '';
    if (!humanIds.has(archetype))
      at(`roster.${k}.archetype`, `${w} roster has unknown human '${archetype}'.`);
    else if (
      humanRole((b.humans ?? []).find((h) => h?.id === archetype) as HumanDef | undefined) ===
      'leader'
    )
      leaders++;
    if (m.recruitRoleId && !roles.has(m.recruitRoleId))
      at(`roster.${k}.recruitRoleId`, `${w} roster has unknown background '${m.recruitRoleId}'.`);
    if (isChildLook(m.outfit) || isChildLook(m.archetype))
      at(`roster.${k}`, `${w} roster can't hold a child (no child NPCs in crews).`);
  });
  if (leaders > 1) at('roster', `${w} has more than one leader.`);
  if (c.tradeTableId && !tables.has(c.tradeTableId))
    at('tradeTableId', `${w} has unknown trade table '${c.tradeTableId}'.`);
  if (c.stashTableId && !tables.has(c.stashTableId))
    at('stashTableId', `${w} has unknown stash table '${c.stashTableId}'.`);
  if ((c.priceMult ?? 0) < 0) at('priceMult', `${w} priceMult is negative.`);
}

function tell(t: OccupiedTellDef, at: At) {
  const w = `Tell '${t.id ?? ''}'`;
  const gate = t.gate ?? '';
  if (!(OCCUPIED_GATES as readonly string[]).includes(gate))
    at('gate', `${w} has unknown gate '${gate}' (yard, defences or inside).`);
  if (!DISPOSITIONS.includes(t.disposition ?? ''))
    at('disposition', `${w} has unknown disposition '${t.disposition ?? ''}'.`);
  if (!(t.text ?? '').trim()) at('text', `${w} needs text.`);
  if (t.map && gate !== 'yard') at('map', `${w} is a map tell, so it must be a yard tell.`);
}

function rulesBlock(r: Required<OccupiedRulesDef>, b: Bundle, roles: Set<string>, at: At) {
  const w = 'Occupied rules';
  if (!isChance(r.chancePerDestination))
    at('chancePerDestination', `${w} chancePerDestination must be in 0..1.`);
  if (r.minPerSet < 0 || r.maxPerSet < r.minPerSet || r.maxPerSet > 2)
    at('maxPerSet', `${w} need 0 ≤ minPerSet ≤ maxPerSet ≤ 2 (the specials cap).`);
  if (r.minRooms < 1) at('minRooms', `${w} minRooms must be at least 1.`);
  // `r.TrueTells?.TwoChance ?? 0`: an explicit null block reads as 0 (in range).
  const tt =
    r.trueTells === null
      ? { twoChance: 0, herringChance: 0 }
      : { twoChance: 0.75, herringChance: 0.4, ...r.trueTells };
  if (!isChance(tt.twoChance ?? 0) || !isChance(tt.herringChance ?? 0))
    at('trueTells', `${w} trueTells chances must be in 0..1.`);
  if (r.tradeOffers < 0 || r.maxTrades < 0 || r.stashRolls < 0)
    at('tradeOffers', `${w} tradeOffers, maxTrades and stashRolls can't be negative.`);
  if (r.maxRecruits < 0 || r.maxRecruits > 1)
    at('maxRecruits', `${w} maxRecruits must be 0 or 1 (one recruit per building).`);
  if (r.maxHumansAlive < 1 || r.maxHumansAlive > 6)
    at('maxHumansAlive', `${w} maxHumansAlive must be 1..6.`);
  if (r.fairPriceMult <= 0) at('fairPriceMult', `${w} fairPriceMult must be above 0.`);
  if (r.madPriceMin <= 0 || r.madPriceMax < r.madPriceMin)
    at('madPriceMin', `${w} need 0 < madPriceMin ≤ madPriceMax.`);
  for (const [k, v] of Object.entries(r.dispositionPriceMult ?? {})) {
    if (!DISPOSITIONS.includes(k))
      at(`dispositionPriceMult.${k}`, `${w} dispositionPriceMult has unknown disposition '${k}'.`);
    if (v <= 0)
      at(`dispositionPriceMult.${k}`, `${w} dispositionPriceMult '${k}' must be above 0.`);
  }
  for (const [k, v] of Object.entries(r.zombieMult ?? {})) {
    if (k !== 'default' && !DISPOSITIONS.includes(k))
      at(`zombieMult.${k}`, `${w} zombieMult has unknown disposition '${k}'.`);
    if (v < 0) at(`zombieMult.${k}`, `${w} zombieMult '${k}' is negative.`);
  }
  if (r.surrenderAt < 0 || r.moraleAllyDown < 0 || r.moraleLowHealth < 0)
    at('surrenderAt', `${w} morale numbers can't be negative.`);
  if (
    !isChance(r.lowHealthFrac) ||
    !isChance(r.lowCoverBlock) ||
    !isChance(r.lowCoverBlockCrouched)
  )
    at('lowHealthFrac', `${w} lowHealthFrac and the cover blocks must be in 0..1.`);
  if (r.zombieIgnoreM < 0 || r.talkRingM <= 0)
    at('talkRingM', `${w} need zombieIgnoreM ≥ 0 and talkRingM above 0.`);
  if (r.captiveRoleId && !roles.has(r.captiveRoleId))
    at('captiveRoleId', `${w} captiveRoleId '${r.captiveRoleId}' is not a background.`);
  const chains = idsIn(b.beatChains);
  for (const [field, id] of [
    ['gateChainId', r.gateChainId],
    ['talkChainId', r.talkChainId],
    ['surrenderChainId', r.surrenderChainId],
  ] as const)
    if (id && !chains.has(id)) at(field, `${w} ${field} '${id}' is not a beat chain.`);

  if (!r.enabled) return;
  const categories = idsIn(b.categories);
  if (!r.categories || r.categories.length === 0)
    at('categories', `${w} are enabled, so they need categories.`);
  for (const c of r.categories ?? [])
    if (!categories.has(c)) at('categories', `${w} have unknown category '${c}'.`);
  if ((b.crews ?? []).length === 0) at('', `${w} are enabled, so they need crews.`);
  const stances = r.stances ?? {};
  for (const d of OCCUPIED_DISPOSITIONS) {
    if (d === 'quiet') continue;
    const byApproach = stances[d];
    if (byApproach == null) {
      at(`stances.${d}`, `${w} stances are missing '${d}'.`);
      continue;
    }
    for (const a of OCCUPIED_APPROACHES) {
      const weights = byApproach[a];
      if (weights == null) {
        at(`stances.${d}.${a}`, `${w} stances '${d}' are missing approach '${a}'.`);
        continue;
      }
      let sum = 0;
      for (const [s, v] of Object.entries(weights)) {
        if (!(OCCUPIED_STANCES as readonly string[]).includes(s))
          at(`stances.${d}.${a}`, `${w} stances '${d}.${a}' have unknown stance '${s}'.`);
        else if (v < 0) at(`stances.${d}.${a}.${s}`, `${w} stances '${d}.${a}.${s}' is negative.`);
        else sum += v;
      }
      if (sum <= 0) at(`stances.${d}.${a}`, `${w} stances '${d}.${a}' need a weight above 0.`);
    }
  }
  for (const key of Object.keys(stances))
    if (!DISPOSITIONS.includes(key))
      at(`stances.${key}`, `${w} stances have unknown disposition '${key}'.`);
}
